// src/components/Projeto/ProjetoDetails.jsx - Versão Atualizada
import React, { useEffect, useState } from 'react';
import { obterProjeto } from '../../services/projetoService';
import { useParams, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Button,
  Paper,
  Snackbar
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Upload as UploadIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  GetApp as GetAppIcon
} from '@mui/icons-material';

import RelatorioSimilaridadeProjeto from './RelatorioSimilaridadeProjeto';

const ProjetoDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projeto, setProjeto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'info' });

  // --------------------------------
  // FUNÇÃO APRIMORADA PARA BAIXAR A CREDENCIAL
  // --------------------------------
  const handleDownloadCredencial = async (projetoId) => {
    try {
      // Mostrar estado de carregamento (opcional)
      // setDownloadingPdf(true);
      
      // Obter token do localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Você precisa estar autenticado para baixar a credencial.');
        return;
      }
      
      console.log('Iniciando download da credencial para o projeto:', projetoId);
      
      // Fazer a requisição normal (agora o backend retornará JSON com Base64)
     const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.zenkroll.co.mz';
const response = await fetch(`${baseUrl}/api/projetos/${projetoId}/credencial`, {
       method: 'GET',
      headers: {
       'Authorization': `Bearer ${token}`,
        'Accept': 'application/json' // Importante para indicar que esperamos JSON
       },
     });
      
      if (!response.ok) {
        throw new Error(`Erro ao baixar PDF: ${response.status} - ${response.statusText}`);
      }
      
      // Obter o JSON que contém o PDF em Base64
      const data = await response.json();
      
      if (!data.success || !data.fileData) {
        throw new Error('Resposta inválida do servidor');
      }
      
      console.log('Dados recebidos com sucesso, tamanho Base64:', data.fileData.length);
      
      // Converter Base64 para Blob
      const binaryString = window.atob(data.fileData);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Criar Blob com o tipo MIME correto
      const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
      console.log('Tamanho do blob:', pdfBlob.size, 'bytes');
      
      // Criar URL do objeto
      const fileURL = URL.createObjectURL(pdfBlob);
      
      // MÉTODO 1: Forçar download
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = data.fileName || `credencial-projeto-${projetoId}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // MÉTODO 2: Também abrir em nova aba para visualização
      window.open(fileURL, '_blank');
      
      // Limpeza
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(fileURL);
      }, 200);
      
      return true;
    } catch (error) {
      console.error('Erro no processo de download:', error);
      alert(`Erro ao baixar a credencial: ${error.message}`);
      return false;
    } finally {
      // Desativar estado de carregamento
      // setDownloadingPdf(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await obterProjeto(id);
        setProjeto(response.data);
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar projeto:', err);
        setError('Não foi possível carregar os detalhes do projeto.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!projeto) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Projecto não encontrado.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Detalhes do Projecto
      </Typography>

      <Card>
        <CardContent>
          {/* Título e Status */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              {projeto.proposta.titulo}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {projeto.status === 'APROVADO' && !projeto.monografia && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<UploadIcon />}
                  onClick={() => navigate(`/monografias/submeter?projetoId=${projeto.id}`)}
                >
                  Submeter Monografia
                </Button>
              )}
              <Chip
                label={projeto.status}
                color={projeto.status === 'APROVADO' ? 'success' : 'warning'}
                sx={{ fontWeight: 'medium' }}
              />
            </Box>
          </Box>

          {/* Informações Básicas */}
          <Box sx={{ display: 'grid', gap: 2, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PersonIcon color="action" />
              <Typography variant="body1">
                <strong>Estudante:</strong> {projeto.proposta.estudante.nome}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SchoolIcon color="action" />
              <Typography variant="body1">
                <strong>Supervisor:</strong> {projeto.supervisor.Usuario.nome} ({projeto.supervisor.area_especializacao})
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BusinessIcon color="action" />
              <Typography variant="body1">
                <strong>Unidade Orgânica:</strong> {projeto.unidadeOrganica.nome}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CalendarIcon color="action" />
              <Typography variant="body1">
                <strong>Submetido em:</strong> {new Date(projeto.created_at).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>

          {/* Alerta de Aprovação */}
          {projeto.status === 'APROVADO' && (
            <Alert 
              severity="success" 
              icon={<CheckCircleIcon />}
              sx={{ mb: 4 }}
            >
              <AlertTitle>Projecto Aprovado</AlertTitle>
              Projecto aprovado em {new Date(projeto.updated_at).toLocaleDateString()}
              {!projeto.monografia && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Você já pode submeter sua monografia.
                </Typography>
              )}
            </Alert>
          )}

          {/* SE A CREDENCIAL FOI EMITIDA, MOSTRAR BOTÃO PARA BAIXAR */}
          {projeto.status === 'APROVADO' && projeto.credencial_emitida && (
            <Alert severity="info" sx={{ mb: 4 }}>
              <AlertTitle>Credencial Disponível</AlertTitle>
              <Typography variant="body2" sx={{ mb: 2 }}>
                A credencial de coleta de dados já foi emitida. Clique abaixo para baixar:
              </Typography>
              <Button
                variant="contained"
                color="success"
                startIcon={<GetAppIcon />}
                onClick={() => handleDownloadCredencial(projeto.id)}
                disabled={downloadingPdf}
              >
                {downloadingPdf ? 'Processando...' : 'Baixar Credencial'}
              </Button>
            </Alert>
          )}

          <Divider sx={{ my: 4 }} />

          {/* Detalhes da Proposta */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Detalhes da Proposta
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ p: 3, bgcolor: 'background.default' }}
            >
              <Typography variant="body1">
                <strong>Problema de Pesquisa:</strong><br />
                {projeto.proposta.problemaPesquisa}
              </Typography>
            </Paper>
          </Box>

          {/* Observações */}
          {projeto.observacoes && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Observações
              </Typography>
              <Paper 
                variant="outlined" 
                sx={{ p: 3, bgcolor: 'background.default' }}
              >
                <Typography variant="body1">
                  {projeto.observacoes}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Relatório de Similaridade — apenas chefe de departamento */}
          {user?.tipo === 'chefe_departamento' && (
            <RelatorioSimilaridadeProjeto projetoId={id} projeto={projeto} />
          )}

          {/* Botões de Ação */}
          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            {projeto.documento_projeto && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<DescriptionIcon />}
              href={`${window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.zenkroll.co.mz'}/api/documentos/${projeto.documento_projeto}`}
                target="_blank"
              >
                Visualizar Documento do Projecto
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
            >
              Voltar
            </Button>
          </Box>
        </CardContent>
      </Card>
      
      {/* Notificação de feedback */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        message={notification.message}
      />
    </Box>
  );
};

export default ProjetoDetails;